"use client";

import { useMemo, useState } from "react";
import { Frame, Slider, Verdict, Note, money } from "./parts";

const L = {
    fr: {
        title: "Vente unique ou abonnement : quand l'abonnement passe devant",
        oneOff: "Prix de la vente unique",
        monthly: "Prix mensuel de l'abonnement",
        churn: "Résiliations par mois",
        crossover: "L'abonnement passe devant au mois",
        lifetime: "Durée de vie moyenne d'un client",
        months: "mois",
        never: "jamais",
        oneOffLabel: "Vente unique",
        subLabel: "Abonnement",
        good: "Le client reste largement au-delà du croisement : l'abonnement rapporte plus.",
        warn: "Le croisement arrive juste avant le départ moyen. La marge est mince.",
        bad: "Le client moyen part avant le croisement : ici, la vente unique rapporte plus.",
        note: "La durée de vie moyenne est déduite du taux de résiliation, dont elle est l'inverse. Les deux courbes ignorent volontairement les frais de paiement, identiques dans les deux cas.",
    },
    en: {
        title: "One-off or subscription: when the subscription pulls ahead",
        oneOff: "One-off price",
        monthly: "Monthly subscription price",
        churn: "Cancellations per month",
        crossover: "Subscription overtakes in month",
        lifetime: "Average customer lifetime",
        months: "months",
        never: "never",
        oneOffLabel: "One-off sale",
        subLabel: "Subscription",
        good: "Customers stay well past the crossover: the subscription earns more.",
        warn: "The crossover lands just before the average departure. The margin is thin.",
        bad: "The average customer leaves before the crossover: here, the one-off sale earns more.",
        note: "Average lifetime is derived from the cancellation rate, of which it is the inverse. Both curves deliberately ignore payment fees, identical either way.",
    },
};

const W = 560;
const H = 220;
const PAD = { l: 46, r: 12, t: 12, b: 26 };
const SPAN = 36; // mois affichés

export default function SubscriptionCrossover({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [oneOff, setOneOff] = useState(240);
    const [monthly, setMonthly] = useState(20);
    const [churn, setChurn] = useState(5);

    const r = useMemo(() => {
        const lifetime = 100 / churn;
        // Le cumul de l'abonnement est linéaire : mois × prix mensuel.
        const crossover = monthly > 0 ? oneOff / monthly : Infinity;
        const max = Math.max(oneOff, monthly * SPAN);
        return { lifetime, crossover, max };
    }, [oneOff, monthly, churn]);

    const x = (m) => PAD.l + (m / SPAN) * (W - PAD.l - PAD.r);
    const y = (v) => H - PAD.b - (v / r.max) * (H - PAD.t - PAD.b);

    const crossVisible = r.crossover <= SPAN;
    const tone =
        r.lifetime >= r.crossover * 1.3
            ? "good"
            : r.lifetime >= r.crossover
              ? "warn"
              : "bad";

    return (
        <Frame icon="graph-up-linear" title={t.title}>
            <div className="grid sm:grid-cols-[1fr_auto] gap-5 items-center">
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    className="w-full h-auto"
                    role="img"
                    aria-label={t.title}
                >
                    {/* Axes */}
                    <line x1={PAD.l} y1={H - PAD.b} x2={W - PAD.r} y2={H - PAD.b} stroke="currentColor" strokeOpacity="0.2" />
                    <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={H - PAD.b} stroke="currentColor" strokeOpacity="0.2" />

                    {/* Vente unique : plate, encaissée au mois 0 */}
                    <line x1={x(0)} y1={y(oneOff)} x2={x(SPAN)} y2={y(oneOff)} stroke="currentColor" strokeOpacity="0.45" strokeWidth="2" strokeDasharray="5 4" />

                    {/* Abonnement : cumul linéaire */}
                    <line x1={x(0)} y1={y(0)} x2={x(SPAN)} y2={y(monthly * SPAN)} stroke="#E8511D" strokeWidth="2.5" />

                    {/* Durée de vie moyenne */}
                    {r.lifetime <= SPAN && (
                        <>
                            <line x1={x(r.lifetime)} y1={PAD.t} x2={x(r.lifetime)} y2={H - PAD.b} stroke="currentColor" strokeOpacity="0.3" strokeDasharray="3 3" />
                            <text x={x(r.lifetime)} y={PAD.t + 10} textAnchor="middle" fontSize="10" fill="currentColor" fillOpacity="0.6">
                                {Math.round(r.lifetime)} {t.months}
                            </text>
                        </>
                    )}

                    {/* Croisement */}
                    {crossVisible && (
                        <circle cx={x(r.crossover)} cy={y(oneOff)} r="5" fill="none" stroke="#E8511D" strokeWidth="2" />
                    )}

                    <text x={PAD.l - 6} y={y(oneOff) + 3} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.6">
                        {Math.round(oneOff)}
                    </text>
                    <text x={W - PAD.r} y={H - 8} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.6">
                        {SPAN} {t.months}
                    </text>
                </svg>

                <div className="space-y-4 sm:w-56">
                    <Slider label={t.oneOff} value={oneOff} onChange={setOneOff} min={20} max={600} step={10} display={money(oneOff, lang)} />
                    <Slider label={t.monthly} value={monthly} onChange={setMonthly} min={5} max={80} step={1} display={money(monthly, lang)} />
                    <Slider label={t.churn} value={churn} onChange={setChurn} min={1} max={20} step={1} display={`${churn} %`} />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.crossover}</div>
                    <div className="text-2xl font-bold text-primary tabular-nums">
                        {crossVisible ? Math.ceil(r.crossover) : t.never}
                    </div>
                </div>
                <div className="rounded-xl bg-gray-100/70 dark:bg-white/[0.04] p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.lifetime}</div>
                    <div className="text-2xl font-bold tabular-nums dark:text-white">
                        {Math.round(r.lifetime)} {t.months}
                    </div>
                </div>
            </div>

            <Verdict tone={tone}>{t[tone]}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
